/**
 * Server Connection Service
 * Handles WebSocket connection to the AtelierCode central server for multi-machine support.
 * This is completely optional - the app works fully offline without it.
 */

import { useMachineStore } from '../stores/machineStore';
import { useSettingsStore } from '../stores/settingsStore';
import { isLocalShell } from '@/lib/platform';
import { getBackend } from './backend';
import { logger } from '@/lib/logger';

// Message types matching the server
type WSMessageType =
  | 'auth'
  | 'auth_response'
  | 'register_machine'
  | 'machine_registered'
  | 'heartbeat'
  | 'heartbeat_ack'
  | 'list_machines'
  | 'machines_list'
  | 'delete_machine'
  | 'delete_machine_response'
  | 'rename_machine'
  | 'rename_machine_response'
  | 'connect_to_machine'
  | 'connection_request'
  | 'connection_accepted'
  | 'connection_rejected'
  | 'rtc_offer'
  | 'rtc_answer'
  | 'rtc_ice_candidate'
  | 'machine_online'
  | 'machine_offline'
  | 'error';

interface WSMessage<T = unknown> {
  type: WSMessageType;
  id?: string;
  payload: T;
}

interface AuthPayload {
  token: string;
}

interface AuthResponse {
  success: boolean;
  user?: {
    id: string;
    email: string;
    username: string;
    roles: string[];
  };
  error?: string;
}

interface MachineCapabilities {
  hasGit: boolean;
  hasNode: boolean;
  hasRust: boolean;
  hasPython: boolean;
}

interface RegisterMachinePayload {
  name: string;
  platform: 'windows' | 'macos' | 'linux';
  capabilities: MachineCapabilities;
}

export interface MachineInfo {
  id: string;
  name: string;
  platform: 'windows' | 'macos' | 'linux';
  isOnline: boolean;
  lastSeen: Date;
  isOwn: boolean;
}

type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'authenticated';

type MessageHandler = (message: WSMessage) => void;

class ServerConnection {
  private ws: WebSocket | null = null;
  private state: ConnectionState = 'disconnected';
  private reconnectAttempts = 0;
  private reconnectDelay = 1000;
  private maxReconnectDelay = 60000; // Cap at 1 minute
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private healthCheckInterval: NodeJS.Timeout | null = null;
  private pendingRequests = new Map<string, { resolve: (value: unknown) => void; reject: (error: Error) => void }>();
  private messageHandlers: MessageHandler[] = [];
  private intentionalDisconnect = false;
  private shouldBeConnected = false; // Track if we should maintain a connection
  private lastHealthCheckTime = Date.now(); // Track time for sleep detection
  private visibilityHandler: (() => void) | null = null;

  get connectionState(): ConnectionState {
    return this.state;
  }

  get isConnected(): boolean {
    return this.state === 'connected' || this.state === 'authenticated';
  }

  get isAuthenticated(): boolean {
    return this.state === 'authenticated';
  }

  /**
   * Connect to the server. Safe to call even if server is unavailable.
   */
  async connect(serverUrl: string): Promise<boolean> {
    if (this.ws && this.state !== 'disconnected') {
      logger.debug('ServerConnection', 'Already connected or connecting');
      return this.isConnected;
    }

    this.intentionalDisconnect = false;
    this.shouldBeConnected = true;
    this.reconnectAttempts = 0;
    this.state = 'connecting';
    this.startHealthCheck();
    this.updateStore();

    return new Promise((resolve) => {
      try {
        this.ws = new WebSocket(serverUrl);

        this.ws.onopen = () => {
          logger.debug('ServerConnection', 'Connected to server');
          this.state = 'connected';
          this.reconnectAttempts = 0;
          this.startHeartbeat();
          this.updateStore();
          resolve(true);
        };

        this.ws.onclose = () => {
          logger.debug('ServerConnection', 'Disconnected from server');
          this.handleDisconnect();
        };

        this.ws.onerror = (error) => {
          logger.warn('ServerConnection', 'WebSocket error (server may be unavailable):', error);
          // Don't throw - just resolve false to indicate connection failed
          if (this.state === 'connecting') {
            this.state = 'disconnected';
            this.updateStore();
            resolve(false);
          }
        };

        this.ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data) as WSMessage;
            this.handleMessage(message);
          } catch (error) {
            logger.error('ServerConnection', 'Failed to parse message:', error);
          }
        };

        // Timeout for connection attempt
        setTimeout(() => {
          if (this.state === 'connecting') {
            logger.debug('ServerConnection', 'Connection timeout');
            this.ws?.close();
            this.state = 'disconnected';
            this.updateStore();
            resolve(false);
          }
        }, 5000);
      } catch (error) {
        logger.warn('ServerConnection', 'Failed to create WebSocket:', error);
        this.state = 'disconnected';
        this.updateStore();
        resolve(false);
      }
    });
  }

  /**
   * Disconnect from server
   */
  disconnect(): void {
    this.intentionalDisconnect = true;
    this.shouldBeConnected = false;
    this.stopHeartbeat();
    this.stopHealthCheck();
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.state = 'disconnected';
    // intentionalDisconnect + shouldBeConnected=false already prevent auto-reconnect
    useMachineStore.getState().setWebrtcReady(false);
    this.updateStore();
  }

  /**
   * Authenticate with Keycloak access token
   */
  async loginWithToken(token: string): Promise<AuthResponse> {
    const response = await this.sendRequest<AuthPayload, AuthResponse>('auth', { token });
    if (response.success) {
      this.state = 'authenticated';
      this.updateStore();
    }
    return response;
  }

  /**
   * Register this machine with the server
   */
  async registerMachine(name: string): Promise<{ machineId: string; name: string } | null> {
    if (!this.isAuthenticated) {
      logger.warn('ServerConnection', 'Must be authenticated to register machine');
      return null;
    }

    const platform = this.detectPlatform();
    const capabilities = await this.detectCapabilities();

    const response = await this.sendRequest<RegisterMachinePayload, { machineId: string; name: string }>(
      'register_machine',
      { name, platform, capabilities }
    );

    if (response.machineId) {
      useMachineStore.getState().setLocalMachineId(response.machineId);
    }

    return response;
  }

  /**
   * Get list of user's machines
   */
  async listMachines(): Promise<MachineInfo[]> {
    if (!this.isAuthenticated) {
      return [];
    }

    const response = await this.sendRequest<Record<string, never>, { machines: MachineInfo[] }>('list_machines', {});
    return response.machines || [];
  }

  /**
   * Delete a machine
   */
  async deleteMachine(machineId: string): Promise<boolean> {
    if (!this.isAuthenticated) {
      return false;
    }

    const response = await this.sendRequest<{ machineId: string }, { success: boolean }>('delete_machine', { machineId });
    return response.success;
  }

  /**
   * Rename a machine
   */
  async renameMachine(machineId: string, newName: string): Promise<boolean> {
    if (!this.isAuthenticated) {
      return false;
    }

    const response = await this.sendRequest<{ machineId: string; newName: string }, { success: boolean }>('rename_machine', { machineId, newName });
    return response.success;
  }

  /**
   * Request connection to a remote machine
   */
  async connectToMachine(targetMachineId: string): Promise<boolean> {
    if (!this.isAuthenticated) {
      logger.warn('ServerConnection', 'Must be authenticated to connect to machine');
      return false;
    }

    // Note: machineId may be null for web clients that haven't registered as a machine
    // They can still initiate connections to desktop machines
    logger.debug('ServerConnection', 'Requesting connection to machine:', targetMachineId);
    this.send({ type: 'connect_to_machine', payload: { targetMachineId } });
    // Connection flow continues via message handlers
    return true;
  }

  /**
   * Add a message handler for incoming messages
   */
  onMessage(handler: MessageHandler): () => void {
    this.messageHandlers.push(handler);
    return () => {
      const index = this.messageHandlers.indexOf(handler);
      if (index >= 0) {
        this.messageHandlers.splice(index, 1);
      }
    };
  }

  private handleMessage(message: WSMessage): void {
    // Handle response to pending request
    if (message.id && this.pendingRequests.has(message.id)) {
      const { resolve } = this.pendingRequests.get(message.id)!;
      this.pendingRequests.delete(message.id);
      resolve(message.payload);
      return;
    }

    // Handle specific message types
    switch (message.type) {
      case 'machine_online':
      case 'machine_offline':
        this.handleMachineStatusChange(message);
        break;
      case 'connection_request':
        this.handleConnectionRequest(message);
        break;
      case 'error':
        logger.error('ServerConnection', 'Server error:', message.payload);
        break;
    }

    // Notify all handlers
    for (const handler of this.messageHandlers) {
      handler(message);
    }
  }

  private async handleMachineStatusChange(message: WSMessage): Promise<void> {
    const { machineId, name } = message.payload as { machineId: string; name: string };
    const isOnline = message.type === 'machine_online';

    // Check if this machine is already in our list
    const existingMachines = useMachineStore.getState().machines;
    const machineExists = existingMachines.some(m => m.id === machineId);

    if (machineExists) {
      // Update existing machine status
      useMachineStore.getState().updateMachineStatus(machineId, isOnline);
    } else if (isOnline) {
      // New machine came online - refresh the full list to get all machine info
      logger.debug('ServerConnection', `New machine ${name} came online, refreshing machine list`);
      const machines = await this.listMachines();
      useMachineStore.getState().setMachines(machines);
    }

    logger.debug('ServerConnection', `Machine ${name} is now ${isOnline ? 'online' : 'offline'}`);

    // When our connected machine goes offline, tear down stale WebRTC so the UI
    // reflects the disconnection and auto-reconnect can fire on the next online event
    if (!isOnline) {
      const selectedId = useMachineStore.getState().selectedMachineId;
      if (selectedId === machineId) {
        const { peerConnection } = await import('./peerConnection');
        if (peerConnection.isConnected || peerConnection.lastTargetMachineId === machineId) {
          logger.info('ServerConnection', 'Connected machine went offline, tearing down stale WebRTC');
          peerConnection.teardownStaleConnection();
          useMachineStore.getState().setWebrtcReady(false);
          useMachineStore.getState().setLastConnectedMachine(machineId);
        }
      }
    }

    // Auto-reconnect: If this machine just came online and matches our selection or last connection, reconnect
    if (isOnline) {
      const { lastConnectedMachineId, selectedMachineId, clearLastConnectedMachine, selectMachine } = useMachineStore.getState();

      // Reconnect if this machine is our current selection OR was our last connection
      const shouldReconnect = (selectedMachineId === machineId || lastConnectedMachineId === machineId);
      if (shouldReconnect) {
        logger.debug('ServerConnection', `Auto-reconnecting to machine: ${name}`);

        clearLastConnectedMachine();
        selectMachine(machineId);

        const { peerConnection } = await import('./peerConnection');
        const { switchToWebRTCBackend } = await import('./backend');
        const { useProjectStore } = await import('../stores/projectStore');

        // Force-cleanup any stale WebRTC connection before attempting reconnect
        if (peerConnection.isConnected) {
          peerConnection.teardownStaleConnection();
        }

        useMachineStore.getState().setWebrtcConnecting(true);
        try {
          const connected = await peerConnection.connect(machineId);
          if (connected) {
            logger.debug('ServerConnection', 'Auto-reconnect successful');
            switchToWebRTCBackend();
            await useProjectStore.getState().loadProjects();
            useMachineStore.getState().setWebrtcReady(true);
          } else {
            logger.debug('ServerConnection', 'Auto-reconnect failed, will retry on next online event');
            useMachineStore.getState().setLastConnectedMachine(machineId);
          }
        } catch (error) {
          logger.error('ServerConnection', 'Auto-reconnect error, will retry:', error);
          useMachineStore.getState().setLastConnectedMachine(machineId);
        } finally {
          useMachineStore.getState().setWebrtcConnecting(false);
        }
      }
    }
  }

  private async handleConnectionRequest(message: WSMessage): Promise<void> {
    const { fromMachineId, fromMachineName, connectionId } = message.payload as {
      fromMachineId: string;
      fromMachineName: string;
      connectionId: string;
    };

    // For now, auto-accept connections from user's own machines
    // In the future, could show a confirmation dialog
    logger.debug('ServerConnection', `Connection request from ${fromMachineName} (${fromMachineId})`);

    // Import peerConnection dynamically to avoid circular dependency
    const { peerConnection } = await import('./peerConnection');

    // Initialize peer connection as responder BEFORE accepting
    await peerConnection.acceptConnection(connectionId, fromMachineId);

    // Send acceptance after peer connection is ready
    this.send({
      type: 'connection_accepted',
      payload: { connectionId },
    });

    logger.debug('ServerConnection', `Connection accepted for ${connectionId}`);
  }

  private async sendRequest<TPayload, TResponse>(type: WSMessageType, payload: TPayload): Promise<TResponse> {
    return new Promise((resolve, reject) => {
      const id = crypto.randomUUID();
      this.pendingRequests.set(id, { resolve: resolve as (v: unknown) => void, reject });

      // Timeout after 10 seconds
      setTimeout(() => {
        if (this.pendingRequests.has(id)) {
          this.pendingRequests.delete(id);
          reject(new Error('Request timeout'));
        }
      }, 10000);

      this.send({ type, id, payload });
    });
  }

  /**
   * Send a message to the server (used internally and by peerConnection for signaling)
   */
  send(message: WSMessage): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  private startHeartbeat(): void {
    this.heartbeatInterval = setInterval(() => {
      this.send({ type: 'heartbeat', payload: {} });
    }, 25000);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Start periodic health check to detect and recover from disconnections
   * Runs every 30 seconds
   * Also listens for system wake-up via visibility changes
   */
  private startHealthCheck(): void {
    this.stopHealthCheck();
    this.lastHealthCheckTime = Date.now();

    this.healthCheckInterval = setInterval(() => {
      this.performHealthCheck();
    }, 30000);

    // Listen for visibility changes (tab/app coming to foreground)
    // This often indicates system woke from sleep
    if (typeof document !== 'undefined') {
      this.visibilityHandler = () => {
        if (document.visibilityState === 'visible') {
          logger.debug('ServerConnection', 'App became visible, checking connection...');
          this.handlePossibleWakeUp();
        }
      };
      document.addEventListener('visibilitychange', this.visibilityHandler);
    }

    // Also listen for online events (network reconnected after sleep)
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        logger.debug('ServerConnection', 'Network came online, checking connection...');
        this.handlePossibleWakeUp();
      });
    }
  }

  private stopHealthCheck(): void {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
    }
    if (this.visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
  }

  /**
   * Handle possible wake-up from system sleep
   * Checks if significant time has passed and forces reconnection if needed
   */
  private async handlePossibleWakeUp(): Promise<void> {
    const now = Date.now();
    const timeSinceLastCheck = now - this.lastHealthCheckTime;

    // If more than 60 seconds passed since last check, system likely slept
    if (timeSinceLastCheck > 60000) {
      logger.debug('ServerConnection', `Detected possible wake-up (${Math.round(timeSinceLastCheck / 1000)}s since last check)`);

      // Check if WebSocket is actually still alive
      if (this.ws) {
        // WebSocket might think it's open but actually be dead
        // Force a check by looking at readyState
        if (this.ws.readyState !== WebSocket.OPEN) {
          logger.debug('ServerConnection', 'WebSocket is not open after wake-up, reconnecting...');
          this.handleDisconnect();
        } else {
          // Try sending a heartbeat to verify connection is alive
          // If connection is dead, the heartbeat will fail and trigger disconnect
          logger.debug('ServerConnection', 'Sending heartbeat to verify connection after wake-up');
          this.send({ type: 'heartbeat', payload: {} });
        }
      } else if (this.shouldBeConnected && !this.intentionalDisconnect) {
        // No WebSocket but we should be connected - reconnect
        logger.debug('ServerConnection', 'No WebSocket after wake-up, reconnecting...');
        this.reconnectAttempts = 0;
        await this.tryReconnect();
      }
    }

    this.lastHealthCheckTime = now;
  }

  private async performHealthCheck(): Promise<void> {
    const now = Date.now();
    const timeSinceLastCheck = now - this.lastHealthCheckTime;

    // Check for wake-up (timer should fire every 30s, if much more passed, system slept)
    if (timeSinceLastCheck > 60000) {
      logger.debug('ServerConnection', `Health check detected wake-up (${Math.round(timeSinceLastCheck / 1000)}s gap)`);
      await this.handlePossibleWakeUp();
      return; // handlePossibleWakeUp already handles reconnection
    }

    this.lastHealthCheckTime = now;

    // If we should be connected but aren't, try to reconnect
    if (this.shouldBeConnected && this.state === 'disconnected' && !this.intentionalDisconnect) {
      logger.debug('ServerConnection', 'Health check: disconnected but should be connected, triggering reconnect');
      // Reset reconnect attempts to allow a fresh set of retries
      this.reconnectAttempts = 0;
      await this.tryReconnect();
    }

    // If we think we're connected but the WebSocket is actually closed, handle it
    if (this.ws && this.ws.readyState === WebSocket.CLOSED) {
      logger.debug('ServerConnection', 'Health check: WebSocket is closed, handling disconnect');
      this.handleDisconnect();
    }
  }

  private handleDisconnect(): void {
    this.stopHeartbeat();
    this.ws = null;
    this.state = 'disconnected';
    this.updateStore();

    // Only attempt reconnect if this wasn't intentional and we should be connected
    if (!this.intentionalDisconnect && this.shouldBeConnected) {
      this.reconnectAttempts++;
      // Exponential backoff capped at 60s — never give up
      const delay = Math.min(
        this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
        this.maxReconnectDelay
      );
      logger.debug('ServerConnection', `Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);
      setTimeout(() => this.tryReconnect(), delay);
    }
  }

  private async tryReconnect(): Promise<void> {
    const settings = useSettingsStore.getState();
    const isLocalDesktop = isLocalShell();
    const isWebMode = !isLocalDesktop;
    const serverUrl = isWebMode ? 'wss://api.ateliercode.dev' : settings.server.url;
    const serverEnabled = isWebMode ? true : settings.server.enabled;

    if (!serverEnabled || !serverUrl) {
      return;
    }

    // Ensure we have a fresh token before reconnecting
    const { useAuthStore } = await import('../stores/authStore');
    const token = await useAuthStore.getState().ensureFreshToken() || settings.server.token;
    if (!token) {
      logger.debug('ServerConnection', 'No valid token available for reconnect');
      return;
    }

    const connected = await this.connect(serverUrl);
    if (!connected) return;

    const result = await this.loginWithToken(token);
    // Re-register machine after reconnect (local-desktop mode only)
    if (result.success && settings.server.machineName) {
      if (isLocalDesktop) {
        await this.registerMachine(settings.server.machineName);
      }
    }

    // Trigger PeerConnection recovery if it was connected before the WS drop
    if (result.success) {
      try {
        const { peerConnection } = await import('./peerConnection');

        // Clean up stale host peers whose signaling path died with the old WebSocket
        peerConnection.cleanupStaleHostPeers();

        if (!peerConnection.isConnected && peerConnection.lastTargetMachineId && !peerConnection.wasIntentionalDisconnect) {
          logger.info('ServerConnection', 'WebSocket recovered, triggering PeerConnection reconnect');
          peerConnection.triggerReconnect();
        }
      } catch (err) {
        logger.warn('ServerConnection', 'Failed to trigger PeerConnection recovery:', err);
      }
    }
  }

  private updateStore(): void {
    useMachineStore.getState().setConnectionState(this.state);
  }

  private detectPlatform(): 'windows' | 'macos' | 'linux' {
    const platform = navigator.platform.toLowerCase();
    if (platform.includes('win')) return 'windows';
    if (platform.includes('mac')) return 'macos';
    return 'linux';
  }

  private async detectCapabilities(): Promise<MachineCapabilities> {
    // In a real implementation, we'd check for these via Tauri commands
    // For now, return reasonable defaults
    return {
      hasGit: true,
      hasNode: true,
      hasRust: true,
      hasPython: false,
    };
  }
}

// Singleton instance
export const serverConnection = new ServerConnection();

/**
 * Initialize server connection if enabled in settings.
 * Safe to call - won't break anything if server is unavailable.
 */
export async function initServerConnection(): Promise<void> {
  const settings = useSettingsStore.getState();

  // In web mode, use hardcoded server URL and authStore token. The CEF host is a
  // local-desktop shell (like the old Tauri shell), so it uses the configured
  // server settings rather than the web defaults.
  const isLocalDesktop = isLocalShell();
  const isWebMode = !isLocalDesktop;
  const serverUrl = isWebMode ? 'wss://api.ateliercode.dev' : settings.server.url;
  const serverEnabled = isWebMode ? true : settings.server.enabled;

  if (!serverEnabled || !serverUrl) {
    logger.debug('ServerConnection', 'Server connection disabled or not configured');
    return;
  }

  logger.debug('ServerConnection', 'Attempting to connect to server...', serverUrl);
  const connected = await serverConnection.connect(serverUrl);

  if (!connected) {
    logger.debug('ServerConnection', 'Server unavailable - continuing in offline mode');
    return;
  }

  // Get a fresh token - prefer authStore (Keycloak OIDC) on all platforms,
  // fall back to settings.server.token for manual configuration
  const { useAuthStore } = await import('../stores/authStore');
  let token = await useAuthStore.getState().ensureFreshToken();
  if (!token && !isWebMode) {
    token = settings.server.token;
  }

  // If we have a token, try to authenticate
  if (token) {
    logger.debug('ServerConnection', 'Attempting authentication with token...');
    const result = await serverConnection.loginWithToken(token);
    if (result.success) {
      logger.debug('ServerConnection', 'Authenticated with token');

      // Register this machine (Tauri mode only)
      if (!isWebMode) {
        let machineName = settings.server.machineName;
        // Auto-populate from system hostname if not configured
        if (!machineName) {
          try {
            machineName = await getBackend().system.getHostname();
            useSettingsStore.getState().setMachineName(machineName);
          } catch {
            machineName = 'My Machine';
          }
        }
        logger.debug('ServerConnection', 'Registering machine:', machineName);
        const machineResult = await serverConnection.registerMachine(machineName);
        if (machineResult) {
          logger.debug('ServerConnection', 'Machine registered:', machineResult.machineId);
        } else {
          logger.debug('ServerConnection', 'Machine registration failed');
        }
      }

      // Fetch machine list
      const machines = await serverConnection.listMachines();
      logger.debug('ServerConnection', 'Loaded machines:', machines.length);
      useMachineStore.getState().setMachines(machines);

      // Re-establish WebRTC connection to persisted selected machine (e.g. after page refresh)
      const { selectedMachineId, localMachineId } = useMachineStore.getState();
      if (selectedMachineId && selectedMachineId !== localMachineId) {
        const targetMachine = machines.find((m) => m.id === selectedMachineId);
        if (targetMachine?.isOnline) {
          logger.debug('ServerConnection', 'Re-connecting to persisted machine:', targetMachine.name);
          const { peerConnection } = await import('./peerConnection');
          const { switchToWebRTCBackend } = await import('./backend');
          const { useProjectStore } = await import('../stores/projectStore');
          useMachineStore.getState().setWebrtcConnecting(true);
          try {
            const connected = await peerConnection.connect(selectedMachineId);
            if (connected) {
              logger.debug('ServerConnection', 'Re-connected to persisted machine');
              switchToWebRTCBackend();
              await useProjectStore.getState().loadProjects();
              useMachineStore.getState().setWebrtcReady(true);
            } else {
              // Keep selection but save for auto-reconnect when machine comes back
              logger.debug('ServerConnection', 'Failed to re-connect, will retry when machine comes online');
              useMachineStore.getState().setLastConnectedMachine(selectedMachineId);
            }
          } catch (error) {
            logger.error('ServerConnection', 'Re-connect error, will retry:', error);
            useMachineStore.getState().setLastConnectedMachine(selectedMachineId);
          } finally {
            useMachineStore.getState().setWebrtcConnecting(false);
          }
        } else {
          // Machine is offline but keep selection — auto-reconnect will fire when it comes online
          logger.debug('ServerConnection', 'Persisted machine not online yet, will auto-reconnect');
          useMachineStore.getState().setLastConnectedMachine(selectedMachineId);
        }
      }
    } else {
      logger.debug('ServerConnection', 'Token authentication failed:', result.error);
    }
  } else {
    logger.debug('ServerConnection', 'No token available - not authenticating');
  }
}
